// Layer/selection derivation from the decoded features (T13). Split out of
// `SpatialResultsMapPanel.tsx` so that file stays under the 200-line cap (T31) — pure
// re-derivation, no new behavior: mirrors exactly what previously lived inline in the panel.

import { useMemo } from 'react';
import type { SpatialMapFeature } from '../map/spatialMapTypes';
import { buildResultLayers } from './spatialResultLayers';

export function useSpatialResultsMapSelection(params: {
  decodedFeatures: SpatialMapFeature[];
  cap: number;
  colorByColumnId: string | null;
  selectedRowIndex?: number | null;
  /** T31 — when set, the header "N features" count reflects only these ids (replay-visible),
   *  not every decoded feature. `null` ⇒ no active replay, count = every decoded feature. */
  visibleKeys?: Set<string | number> | null;
}) {
  const { decodedFeatures, cap, colorByColumnId, selectedRowIndex, visibleKeys } = params;

  const { layers, legend, layerIdForFeature } = useMemo(
    () => buildResultLayers({ features: decodedFeatures, cap, colorByColumnId }),
    [decodedFeatures, cap, colorByColumnId],
  );

  const featureById = useMemo(() => {
    const map = new Map<string | number, SpatialMapFeature>();
    for (const f of decodedFeatures) map.set(f.id, f);
    return map;
  }, [decodedFeatures]);

  const selectedFeature = useMemo(() => {
    if (selectedRowIndex === null || selectedRowIndex === undefined) return null;
    const layerId = layerIdForFeature.get(selectedRowIndex);
    return layerId ? { layerId, featureId: selectedRowIndex } : null;
  }, [selectedRowIndex, layerIdForFeature]);

  const renderedCount = useMemo(() => {
    if (!visibleKeys) return decodedFeatures.length;
    let count = 0;
    for (const f of decodedFeatures) if (visibleKeys.has(f.id)) count += 1;
    return count;
  }, [decodedFeatures, visibleKeys]);

  return { layers, legend, featureById, selectedFeature, renderedCount };
}
